import { forwardRef } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import {
  HeartFilledIcon,
  HeartIcon,
  IconButton,
  StarIcon,
  ThumbsUpFilledIcon,
  ThumbsUpIcon,
} from '@buffer-mono/popcorn'

import type {
  ReactionType,
  SupportedReaction,
} from '../../hooks/configuration/useConfigurationQuery'

import styles from './APIReaction.module.css'

// Rest props (aria-haspopup, aria-expanded, aria-controls, data-state, etc.)
// are forwarded to the underlying button so this icon works correctly as a
// Radix Popover trigger via `asChild` — without them the picker isn't
// announced to assistive tech as an expandable control.
type ReactionIconProps = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'onClick' | 'children'
> & {
  likingIcon?: string | null
  isLiked: boolean
  reactionType?: ReactionType
  supportedReactions?: SupportedReaction[]
  /**
   * Noun used in the like label ("Like this {entity}"). Defaults to
   * `'comment'`; Mentions pass `'mention'`. The unlike label ("Remove like")
   * is noun-free and shared.
   */
  entity?: string
  targetLabel?: string
  /**
   * Display name of the source network (e.g. "Instagram"). When set, the
   * tooltip appends the one-way-sync caveat — liking in Buffer pushes to the
   * network, but likes made there never sync back. Used for single-like
   * channels; multi-reaction channels surface the same message in the picker.
   */
  networkName?: string
  loading?: boolean
  active?: boolean
  showTooltip?: boolean
  /**
   * When true, the icon renders at reduced opacity with clicks ignored.
   * Implemented with `aria-disabled` (not native `disabled`, whose
   * `pointer-events: none` would suppress the tooltip) so view-only actors
   * can still hover/focus the icon and read why it's unavailable.
   */
  disabled?: boolean
  /**
   * Optional tooltip that overrides the default "Like this comment" label.
   * Used together with `disabled` to explain why the action is unavailable.
   */
  tooltipOverride?: string
  onClick: (e: React.MouseEvent) => void
}

const getIsImageReaction = (
  isLiked: boolean,
  reactionType: ReactionType | undefined,
  supportedReactions: SupportedReaction[] | undefined,
): boolean =>
  isLiked &&
  reactionType != null &&
  supportedReactions?.some(
    (r) => r.reactionType === reactionType && r.visual.type === 'image',
  ) === true

const getLikingIcons = (
  likingIcon: string | null | undefined,
): { FilledIcon: React.FC; OutlineIcon: React.FC } => {
  switch (likingIcon) {
    case 'heart':
      return { FilledIcon: HeartFilledIcon, OutlineIcon: HeartIcon }
    case 'star':
      return { FilledIcon: StarIcon, OutlineIcon: StarIcon }
    default:
      return { FilledIcon: ThumbsUpFilledIcon, OutlineIcon: ThumbsUpIcon }
  }
}

const renderIcon = (
  isLiked: boolean,
  reactionType: ReactionType | undefined,
  supportedReactions: SupportedReaction[] | undefined,
  likingIcon: string | null | undefined,
): JSX.Element => {
  if (isLiked && reactionType && supportedReactions) {
    const selectedReaction = supportedReactions.find(
      (r) => r.reactionType === reactionType,
    )

    if (selectedReaction && selectedReaction.visual.type === 'image') {
      return (
        <img
          src={selectedReaction.visual.url}
          alt={reactionType}
          className={styles.reactionIconImage}
        />
      )
    }
  }

  const { FilledIcon, OutlineIcon } = getLikingIcons(likingIcon)
  const IconComponent = isLiked ? FilledIcon : OutlineIcon

  return <IconComponent />
}

export const ReactionIcon = forwardRef<HTMLButtonElement, ReactionIconProps>(
  (
    {
      likingIcon,
      isLiked,
      reactionType,
      supportedReactions,
      entity = 'comment',
      targetLabel,
      networkName,
      loading,
      active,
      showTooltip = false,
      disabled = false,
      tooltipOverride,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const isImageReaction = getIsImageReaction(
      isLiked,
      reactionType,
      supportedReactions,
    )

    const tooltipLabel = isLiked ? 'Remove like' : `Like this ${entity}`
    const label = targetLabel
      ? isLiked
        ? `Remove like from ${targetLabel}`
        : `Like ${targetLabel}`
      : tooltipLabel
    const tooltipText = networkName
      ? `${tooltipLabel}. Buffer syncs likes to ${networkName}, but likes added there won't show here.`
      : tooltipLabel
    const resolvedTooltip =
      tooltipOverride ?? (showTooltip ? tooltipText : undefined)

    return (
      <IconButton
        {...rest}
        ref={ref}
        label={label}
        tooltip={resolvedTooltip}
        variant="tertiary"
        size="small"
        className={clsx(
          styles.default,
          active && styles.active,
          isLiked && !isImageReaction && styles.liked,
          isImageReaction && styles.likedImage,
          disabled && styles.viewOnlyDisabled,
        )}
        onClick={(e): void => {
          if (disabled) {
            // Clicking the inert control shouldn't bubble up and expand the card.
            e.stopPropagation()
            return
          }
          onClick(e)
        }}
        disabled={loading}
        aria-disabled={disabled || undefined}
      >
        {renderIcon(isLiked, reactionType, supportedReactions, likingIcon)}
      </IconButton>
    )
  },
)

ReactionIcon.displayName = 'ReactionIcon'
